import { useState } from 'react'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { MonthPeriodPicker } from './shared/MonthPeriodPicker'
import { ChartFormatPicker } from './shared/ChartFormatPicker'
import { ChartFrame, withJsonFormat } from './shared/ChartFrame'
import { DLY_COLUMNS, DayGrid } from './DlyOneMonthView'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useMonthPeriodStore } from '../../store/useMonthPeriodStore'
import { useTimeZoneStore } from '../../store/useTimeZoneStore'
import { useDlyGrafStore } from '../../store/useDlyGrafStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useDlyOneMonthData } from '../../hooks/useDlyOneMonthData'
import { calcMonthFromTo } from '../../hooks/useGrafData'
import { useChartFillBust } from '../../hooks/shared/useChartFillBust'
import { useChartToFill } from '../../hooks/shared/useToFill'
import { useAuthStore } from '../../store/useAuthStore'

const CHART_BASE = import.meta.env.VITE_API_URL || '/api'
const SLUG = 'dly-graf'

function pad2(n) {
  return String(n).padStart(2, '0')
}

// Klouzavé okno posledních 30 dní končící VČEREJŠKEM pro volbu "aktuální" (dnešek se nekreslí)
function calcAktualFromTo() {
  const now = new Date()
  const to = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1))
  const from = new Date(to.getTime() - 30 * 86400000)
  const fmt = d => `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}2359`
  const fmtFrom = d => `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}0000`
  return { from: fmtFrom(from), to: fmt(to) }
}

// Přehled-Graf pro denní data (1 stanice) — graf/tabulka pro zvolený měsíc, viz
// doc/plans/station-group-meas-chart-gran.md bod 2+4. Tabulka (tab "Data") znovupoužívá
// DLY_COLUMNS/DayGrid z DlyOneMonthView — je to stejný obsah jako chip "Přehled", jen dostupný
// i z grafové stránky bez přepínání chipu. Volba "aktuální" přepne graf/tabulku na klouzavé
// okno posledních 30 dní nezávisle na zvoleném měsíci/roce.
export function DlyGrafView({ station }) {
  const { year, month, setYear, setMonth } = useMonthPeriodStore()
  const tz = useTimeZoneStore(s => s.tz)
  const { isAktual, setIsAktual, activeTab, setActiveTab, chartFormat, interactive } = useDlyGrafStore()
  const token = useAuthStore(s => s.token)
  const orientation = useTableOrientationStore(s => s.getOrientation(SLUG))

  const { dataMap, labelMap, isLoading, isError } = useDlyOneMonthData(
    station?.gh_id, year, month, DLY_COLUMNS, isAktual, activeTab === 'data'
  )

  const { from, to } = isAktual ? calcAktualFromTo() : calcMonthFromTo(year, month)
  const fillBust = useChartFillBust()
  const chartUrl = withJsonFormat(
    `${CHART_BASE}/chart?type=nowmulti&cfg=dmulti_dly&station=${station?.gh_id}&from=${from}&to=${to}&timezone=${encodeURIComponent(tz)}&gran=dly`,
    chartFormat
  ) + fillBust

  const [chartMeta, setChartMeta] = useState(null)
  const screenKey = JSON.stringify(['dly-graf', station?.gh_id, year, month, isAktual, tz])
  useChartToFill(screenKey, chartMeta, { gran: 'DLY', live: isAktual }, activeTab === 'graf')

  function handleMonthChange(setter) {
    return value => {
      setter(value)
      setIsAktual(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}

        <div className="flex flex-wrap items-start gap-4">
          <MonthPeriodPicker
            year={year}
            month={month}
            onYearChange={handleMonthChange(setYear)}
            onMonthChange={handleMonthChange(setMonth)}
            obsType="DLY"
            isAktual={isAktual}
            onAktual={() => setIsAktual(true)}
          />

          <div className="flex flex-wrap items-start gap-4 ml-auto">
            {activeTab === 'data'
              ? <TableOrientationToggle slug={SLUG} />
              : <ChartFormatPicker useStore={useDlyGrafStore} />}
          </div>
        </div>

        {/* Záložky */}
        <div className="flex gap-2 border-b border-gray-100 -mb-4 pb-0">
          {['graf', 'data'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'graf' ? 'Graf' : 'Data'}
            </button>
          ))}
        </div>
      </div>

      {/* Obsah záložky */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {activeTab === 'graf' && (
          <div className="overflow-auto">
            <ChartFrame
              key={`${chartUrl}:${token ?? 'anon'}`}
              url={chartUrl}
              gran="dly"
              interactive={interactive}
              onMeta={setChartMeta}
            />
          </div>
        )}

        {activeTab === 'data' && (
          <>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Načítání dat…
              </div>
            )}
            {isError && (
              <p className="text-red-500 text-sm">Chyba při načítání dat</p>
            )}
            {!isLoading && !isError && (
              <DayGrid columns={DLY_COLUMNS} dataMap={dataMap} labelMap={labelMap} orientation={orientation} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
